import type { AccountPeriodEntry } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";

const ACCOUNT_PERIOD_ENTRIES_QUERY = gql`
  query GetAccountPeriodEntries(
    $tenantId: String!
    $accountId: String!
    $startDate: String!
    $endDate: String!
  ) {
    accountPeriodEntries(
      tenantId: $tenantId
      accountId: $accountId
      startDate: $startDate
      endDate: $endDate
    ) {
      journalEntryId
      timestamp
      bookingText
      documentReference
      debit
      credit
      balance
      invoice {
        invoiceId
        invoiceNo
        aiSummary
        totalAmount
        invoiceDate
        dueDate
      }
      counterparty {
        entityId
        entityName
        entityType
      }
    }
  }
`;

export interface GetAccountPeriodEntriesParams {
  accountId?: string | null;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
}

export const useGetAccountPeriodEntries = ({
  accountId,
  startDate,
  endDate,
}: GetAccountPeriodEntriesParams) => {
  const { tenantId } = useTenant();

  // Helper function to convert Date or string to ISO string
  const toISOString = (
    date: Date | string | null | undefined,
  ): string | undefined => {
    if (!date) return undefined;
    if (typeof date === "string") return date;
    return date.toISOString();
  };

  const { data, loading, error, refetch } = useQuery<{
    accountPeriodEntries: AccountPeriodEntry[];
  }>(ACCOUNT_PERIOD_ENTRIES_QUERY, {
    variables: {
      tenantId,
      accountId,
      startDate: toISOString(startDate),
      endDate: toISOString(endDate),
    },
    skip: !tenantId || !accountId || !startDate || !endDate,
  });

  return {
    data: data?.accountPeriodEntries || [],
    loading,
    error,
    refetch,
  };
};
